<template>
  <div class="w-full max-w-md mx-auto px-4 py-6 space-y-6">
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-4 overflow-hidden">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h1 class="font-semibold text-white text-xl">
            {{ $t('Mobile.MobileContractEventsIndex.title') }}
          </h1>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4 space-y-4">
        <RouterLink
          v-if="userStore.hasAccessToRoute('MobileContractEventCreate') && projectsStore.project?.unarchived"
          :to="{ name: 'MobileContractEventCreate' }"
          class="btn btn--primary-blue shadow-md w-full flex items-center justify-center space-x-2"
          data-test="new-contract-event"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

          <span>{{ $t('Mobile.MobileContractEventsIndex.raiseEvent') }}</span>
        </RouterLink>

        <div class="flex flex-col space-y-3">
          <AppSpinner
            v-if="fetchAction.is(States.LOADING)"
            class="p-4 mx-auto"
          />

          <div
            v-else-if="contractEvents.length > 0"
            class="w-full space-y-3"
          >
            <RouterLink
              v-for="event in contractEvents"
              :key="event.id"
              v-slot="{ navigate }"
              :to="{ name: 'MobileContractEventCreate', params: { projectId: projectId, contractEventId: event.id } }"
              custom
            >
              <div
                class="btn p-4 text-left btn--secondary-blue"
                @click="navigate()"
              >
                <div class="flex-1 flex flex-col space-y-1 items-start">
                  <span class="font-semibold text-gray-900">{{ event.code }} - {{ event.name }}</span>

                  <span
                    :class="['text-xs px-2 py-0.5 rounded-full w-fit', ContractEventStatusBadgeClasses[enumKeyFromValue(ContractEventStatus, event.status)] ?? 'bg-gray-200 text-gray-700']"
                  >
                    {{ ContractEventStatusTitles[enumKeyFromValue(ContractEventStatus, event.status)] }}
                  </span>

                  <div class="flex items-center space-x-2 text-xs text-gray-500">
                    <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-3.5 h-3.5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

                    <span>{{ event.createdAt.toLocaleString() }}</span>
                  </div>

                  <div
                    v-if="showRaiser && event.user"
                    class="flex items-center space-x-1 text-xs text-gray-500 mt-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>

                    <span>{{ event.user.name }}</span>
                  </div>
                </div>
              </div>
            </RouterLink>
          </div>

          <div
            v-else
            class="text-center py-8"
          >
            <div class="flex flex-col items-center space-y-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-300">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <div class="text-gray-500">
                <p class="font-medium">
                  {{ $t('Mobile.MobileContractEventsIndex.noEvents') }}
                </p>

                <p class="text-sm">
                  {{ $t('Mobile.MobileContractEventsIndex.raiseFirst') }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="contractEventsPaginator"
            class="pt-2"
          >
            <nav class="flex items-center justify-center space-x-0.5">
              <button
                type="button"
                class="px-3 py-2 text-sm font-medium border rounded-l-md"
                :class="contractEventsPaginator.links.prev
                  ? 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  : 'text-gray-400 bg-white border-gray-300 cursor-not-allowed'"
                :disabled="!contractEventsPaginator.links.prev"
                @click="goToPage(contractEventsPaginator.meta.current_page - 1)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-3.5 w-3.5">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
              </button>

              <button
                v-for="(pageLink, index) in paginationLinks"
                :key="index"
                type="button"
                class="px-3 py-2 text-sm font-medium border"
                :class="pageLink.active
                  ? 'text-white bg-blue-600 border-blue-600'
                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'"
                @click="goToPage(Number(pageLink.label))"
              >
                {{ pageLink.label }}
              </button>

              <button
                type="button"
                class="px-3 py-2 text-sm font-medium border rounded-r-md"
                :class="contractEventsPaginator.links.next
                  ? 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  : 'text-gray-400 bg-white border-gray-300 cursor-not-allowed'"
                :disabled="!contractEventsPaginator.links.next"
                @click="goToPage(contractEventsPaginator.meta.current_page + 1)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-3.5 w-3.5">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue } from '@/helpers';
import { t } from '@/i18n';
import ContractEvent, {
  ContractEventStatus,
  ContractEventStatusBadgeClasses,
  ContractEventStatusTitles,
} from '@/models/ContractEvent';
import { useContractEventsStore } from '@/store/contractEvents';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useRouteQuery } from '@vueuse/router';
import { computed, onMounted, ref } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const userStore = useUserStore();
const projectsStore = useProjectsStore();
const contractEventsStore = useContractEventsStore();
const fetchAction = useStoreApiAction(contractEventsStore.fetchContractEvents);
const pageQuery = useRouteQuery('page', '1', { transform: Number });
const contractEventsPaginator = ref<ResourcePaginator<ContractEvent> | undefined>(undefined);

const contractEventIds = computed(() => {
  return contractEventsPaginator.value?.data.map((event) => event.id) ?? [];
});

const contractEvents = computed(() => {
  return contractEventsStore.models
    .whereIn('id', contractEventIds.value)
    .with('user')
    .orderBy('created_at', 'desc')
    .get();
});

const paginationLinks = computed(() => {
  if(!contractEventsPaginator.value) {
    return [];
  }

  return contractEventsPaginator.value.meta.links.filter((link) => {
    return !link.label.includes('Previous') && !link.label.includes('Next');
  });
});

const showRaiser = computed(() => {
  return !userStore.isRole(['engineer', 'site_manager'], false);
});

function canEditEvent(event: { status: number }): boolean {
  return event.status === ContractEventStatus.pending;
}

const loadPage = async (page: number) => {
  const data = await fetchAction.request(props.projectId, { page });

  contractEventsPaginator.value = data;
};

const goToPage = (page: number) => {
  if(!contractEventsPaginator.value || page < 1 || page > contractEventsPaginator.value.meta.last_page) {
    return;
  }

  pageQuery.value = page;
  loadPage(page);
};

onMounted(() => {
  loadPage(pageQuery.value);
});

const navStore = useNavStore();

navStore.backRoute = { name: 'MobileProjectPage' };
</script>
